import React, {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react'

import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { canvasPreview, canvasToBlob, resizeBlob } from './canvas-preview'
import { makeStyles } from 'tss-react/mui'

import './cropped-image-input.css'
import 'react-image-crop/dist/ReactCrop.css'

const useStyles = makeStyles()((theme) => ({
  container: {
    marginBottom: theme.spacing(2),
  },
  imageContainer: {
    width: '100%',
  },
  image: {
    height: 180,
    maxWidth: '100%',
    objectFit: 'contain',
  },
  dialog: {},
  control: {},
  removeButton: {
    marginTop: theme.spacing(2),
  },
  sourceImage: {
    width: '100%',
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  preview: {
    border: '1px solid black',
    objectFit: 'contain',
    margin: '0 auto',
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}))

type Props = {
  imageUrl: string
  desiredWidth: number
  desiredHeight: number
  onChange?: (url: string, name: string) => void
  isEditing?: boolean
  onRemove?: () => void
}

const CroppedImageInput = ({
  imageUrl,
  desiredWidth,
  desiredHeight,
  onChange = () => true,
  isEditing = true,
  onRemove: handleRemoveImage = () => true,
}: Props): ReactElement => {
  const { classes } = useStyles()

  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  const [newImageName, setNewImageName] = useState('')
  const [newImageUrl, setNewImageUrl] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  let url = imageUrl || ''
  if (url.startsWith('//')) {
    url = 'https:' + url
  }

  const aspect = desiredWidth / desiredHeight

  const handlePhotoChange = (event: any) => {
    const file = event.target.files[0]
    event.target.value = ''

    if (!file) return

    setNewImageName(file.name)
    setNewImageUrl(URL.createObjectURL(file))
    setErrorMessage('')
    setCrop(undefined)
    setCompletedCrop(undefined)
    setCropDialogOpen(true)
  }

  const handleImageError = () => {
    setErrorMessage(
      'This image could not be opened. Please try a different file type or browser.',
    )
  }

  const onImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget
    setCrop(
      centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 100,
          },
          aspect,
          width,
          height,
        ),
        width,
        height,
      ),
    )
    renderPreview()
  }

  const handleCropComplete = async () => {
    const canvas = previewCanvasRef.current

    if (!canvas) {
      setErrorMessage('Select a crop area before saving.')
      return
    }

    try {
      const croppedBlob = await canvasToBlob(canvas)
      const resizedBlob = await resizeBlob(
        croppedBlob,
        desiredWidth,
        desiredWidth,
      )
      onChange(URL.createObjectURL(resizedBlob), newImageName)
      setCropDialogOpen(false)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? `This image could not be saved. ${error.message}.`
          : 'This image could not be saved.',
      )
    }
  }

  const handleCancel = () => {
    setErrorMessage('')
    setCropDialogOpen(false)
  }

  const renderPreview = () => {
    const t = setTimeout(() => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        try {
          canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop)
        } catch (error) {
          setErrorMessage('This image could not be previewed.')
        }
      }
    }, 100)

    return () => {
      clearTimeout(t)
    }
  }

  useEffect(renderPreview, [completedCrop])

  return (
    <>
      <Dialog className={classes.dialog} open={cropDialogOpen}>
        <DialogTitle id='crop-dialog-title'>Set image crop</DialogTitle>
        <DialogContent>
          <ReactCrop
            aspect={aspect}
            crop={crop}
            keepSelection
            onChange={setCrop}
            onComplete={setCompletedCrop}
          >
            <img
              alt='crop image'
              className={classes.sourceImage}
              onError={handleImageError}
              onLoad={onImageLoad}
              ref={imgRef}
              src={newImageUrl}
            />
          </ReactCrop>
          {!!errorMessage && (
            <Typography className={classes.error} variant='body2'>
              {errorMessage}
            </Typography>
          )}
          {!!completedCrop && (
            <canvas
              className={classes.preview}
              ref={previewCanvasRef}
              style={{
                width: 300,
                height: (desiredHeight / desiredWidth) * 300,
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button disabled={!completedCrop} onClick={handleCropComplete}>
            Accept
          </Button>
        </DialogActions>
      </Dialog>
      <div className={classes.container}>
        <div className={classes.imageContainer}>
          {url ? (
            <img className={classes.image} src={url} />
          ) : (
            <span>No image uploaded</span>
          )}
        </div>
        <input
          accept='image/*'
          id='image-upload-button'
          onChange={handlePhotoChange}
          style={{ display: 'none' }}
          type='file'
        />
        {isEditing && (
          <label htmlFor='image-upload-button'>
            <Button
              className={classes.control}
              component='span'
              variant='contained'
            >
              Upload Image
            </Button>
          </label>
        )}
        {isEditing && url && (
          <div className={classes.removeButton}>
            <Button
              className={classes.control}
              component='span'
              onClick={handleRemoveImage}
              variant='contained'
            >
              Remove Image
            </Button>
          </div>
        )}
      </div>
    </>
  )
}

export default CroppedImageInput
